import { randomUUID } from "node:crypto";
import { mkdir, readFile, rename, unlink, writeFile } from "node:fs/promises";
import { dirname, join } from "node:path";

import type {
  Actual,
  CalibratedProfile,
  Estimate,
  FathomStore,
  Intake,
  ModelPricing,
  Project,
  Reconciliation,
  VelocityBenchmark,
  VelocitySnapshot,
} from "./types.js";

// ── Helpers ─────────────────────────────────────────────────

function generateId(): string {
  return randomUUID();
}

async function ensureDir(path: string): Promise<void> {
  await mkdir(dirname(path), { recursive: true });
}

async function readJsonFile<T>(path: string): Promise<T[]> {
  try {
    const raw = await readFile(path, "utf-8");
    return JSON.parse(raw) as T[];
  } catch (err: unknown) {
    if ((err as NodeJS.ErrnoException).code === "ENOENT") {
      return [];
    }
    throw err;
  }
}

async function writeJsonFile<T>(path: string, data: T[]): Promise<void> {
  await ensureDir(path);
  const tmp = `${path}.${Date.now()}.${Math.random().toString(36).slice(2, 8)}.tmp`;
  await writeFile(tmp, JSON.stringify(data, null, 2), "utf-8");
  try {
    await rename(tmp, path);
  } catch (err) {
    await unlink(tmp).catch(() => {});
    throw err;
  }
}

// ── FileStore ───────────────────────────────────────────────

export class FileStore implements FathomStore {
  private baseDir: string;

  constructor(baseDir: string) {
    this.baseDir = baseDir;
  }

  private path(...segments: string[]): string {
    return join(this.baseDir, ...segments);
  }

  // ── Projects ────────────────────────────────────────────

  async getProject(name: string): Promise<Project | null> {
    const projects = await readJsonFile<Project>(this.path("projects.json"));
    return projects.find((p) => p.name === name) ?? null;
  }

  async upsertProject(project: Omit<Project, "id">): Promise<string> {
    const filePath = this.path("projects.json");
    const projects = await readJsonFile<Project>(filePath);
    const existing = projects.find((p) => p.name === project.name);

    if (existing) {
      Object.assign(existing, project);
      await writeJsonFile(filePath, projects);
      return existing.id;
    }

    const id = generateId();
    projects.push({ ...project, id });
    await writeJsonFile(filePath, projects);
    return id;
  }

  // ── Estimates ───────────────────────────────────────────

  async saveEstimate(estimate: Omit<Estimate, "id">): Promise<string> {
    const filePath = this.path("estimates", `${estimate.projectId}.json`);
    const estimates = await readJsonFile<Estimate>(filePath);
    const id = generateId();
    estimates.push({ ...estimate, id });
    await writeJsonFile(filePath, estimates);
    return id;
  }

  async getEstimates(projectId: string): Promise<Estimate[]> {
    return readJsonFile<Estimate>(
      this.path("estimates", `${projectId}.json`),
    );
  }

  // ── Actuals ─────────────────────────────────────────────

  async saveActual(actual: Omit<Actual, "id">): Promise<string> {
    const filePath = this.path("actuals", `${actual.projectId}.json`);
    const actuals = await readJsonFile<Actual>(filePath);
    const id = generateId();
    actuals.push({ ...actual, id });
    await writeJsonFile(filePath, actuals);
    return id;
  }

  async getActuals(
    projectId: string,
    opts?: { featureId?: string },
  ): Promise<Actual[]> {
    const actuals = await readJsonFile<Actual>(
      this.path("actuals", `${projectId}.json`),
    );
    if (opts?.featureId) {
      return actuals.filter((a) => a.featureId === opts.featureId);
    }
    return actuals;
  }

  // ── Intakes ─────────────────────────────────────────────

  async saveIntake(intake: Omit<Intake, "id">): Promise<string> {
    const filePath = this.path("intakes", `${intake.projectId}.json`);
    const intakes = await readJsonFile<Intake>(filePath);
    const id = generateId();
    intakes.push({ ...intake, id });
    await writeJsonFile(filePath, intakes);
    return id;
  }

  async getIntakes(projectId: string): Promise<Intake[]> {
    return readJsonFile<Intake>(
      this.path("intakes", `${projectId}.json`),
    );
  }

  // ── Velocity Benchmarks ─────────────────────────────────

  async saveBenchmark(
    benchmark: Omit<VelocityBenchmark, "id">,
  ): Promise<string> {
    const filePath = this.path("velocity", "benchmarks.json");
    const benchmarks = await readJsonFile<VelocityBenchmark>(filePath);
    const id = generateId();
    benchmarks.push({ ...benchmark, id });
    await writeJsonFile(filePath, benchmarks);
    return id;
  }

  async getBenchmarks(opts?: {
    category?: string;
    complexity?: string;
  }): Promise<VelocityBenchmark[]> {
    const benchmarks = await readJsonFile<VelocityBenchmark>(
      this.path("velocity", "benchmarks.json"),
    );
    return benchmarks.filter((b) => {
      if (opts?.category && b.category !== opts.category) return false;
      if (opts?.complexity && b.complexity !== opts.complexity) return false;
      return true;
    });
  }

  // ── Velocity Snapshots ──────────────────────────────────

  async saveSnapshot(
    snapshot: Omit<VelocitySnapshot, "id">,
  ): Promise<string> {
    const filePath = this.path("velocity", "snapshots.json");
    const snapshots = await readJsonFile<VelocitySnapshot>(filePath);
    const id = generateId();
    snapshots.push({ ...snapshot, id });
    await writeJsonFile(filePath, snapshots);
    return id;
  }

  async getSnapshots(opts?: {
    projectId?: string;
    period?: string;
  }): Promise<VelocitySnapshot[]> {
    const snapshots = await readJsonFile<VelocitySnapshot>(
      this.path("velocity", "snapshots.json"),
    );
    return snapshots.filter((s) => {
      if (opts?.projectId && s.projectId !== opts.projectId) return false;
      if (opts?.period && s.period !== opts.period) return false;
      return true;
    });
  }

  // ── Reconciliations ─────────────────────────────────────

  async saveReconciliation(
    recon: Omit<Reconciliation, "id">,
  ): Promise<string> {
    const filePath = this.path(
      "reconciliations",
      `${recon.projectId}.json`,
    );
    const recons = await readJsonFile<Reconciliation>(filePath);
    const id = generateId();
    recons.push({ ...recon, id });
    await writeJsonFile(filePath, recons);
    return id;
  }

  async getReconciliations(projectId: string): Promise<Reconciliation[]> {
    return readJsonFile<Reconciliation>(
      this.path("reconciliations", `${projectId}.json`),
    );
  }

  // ── Calibrated Profiles ─────────────────────────────────

  async saveProfile(
    profile: Omit<CalibratedProfile, "id">,
  ): Promise<string> {
    const filePath = this.path("profiles.json");
    const profiles = await readJsonFile<CalibratedProfile>(filePath);

    // Upsert by taskType
    const existing = profiles.find((p) => p.taskType === profile.taskType);
    if (existing) {
      Object.assign(existing, profile);
      await writeJsonFile(filePath, profiles);
      return existing.id;
    }

    const id = generateId();
    profiles.push({ ...profile, id });
    await writeJsonFile(filePath, profiles);
    return id;
  }

  async getProfiles(): Promise<CalibratedProfile[]> {
    return readJsonFile<CalibratedProfile>(this.path("profiles.json"));
  }

  async getProfile(taskType: string): Promise<CalibratedProfile | null> {
    const profiles = await readJsonFile<CalibratedProfile>(
      this.path("profiles.json"),
    );
    return profiles.find((p) => p.taskType === taskType) ?? null;
  }

  // ── Model Pricing ───────────────────────────────────────

  async savePricing(pricing: Omit<ModelPricing, "id">): Promise<string> {
    const filePath = this.path("pricing.json");
    const pricings = await readJsonFile<ModelPricing>(filePath);

    // Upsert by model
    const existing = pricings.find((p) => p.model === pricing.model);
    if (existing) {
      Object.assign(existing, pricing);
      await writeJsonFile(filePath, pricings);
      return existing.id;
    }

    const id = generateId();
    pricings.push({ ...pricing, id });
    await writeJsonFile(filePath, pricings);
    return id;
  }

  async getPricing(model?: string): Promise<ModelPricing[]> {
    const pricings = await readJsonFile<ModelPricing>(
      this.path("pricing.json"),
    );
    if (model) {
      return pricings.filter((p) => p.model === model);
    }
    return pricings;
  }
}
